/**
 * Domain: Visitor Exit Detection
 *
 * Pure functions that encode the state-machine logic for deciding what action
 * to take when an access event arrives. Zero React / side-effect dependencies —
 * fully unit-testable.
 *
 * ZONE-ONLY RULE: `event.direction` is always resolved server-side from the
 * event's zone.entry_type before it reaches the frontend (see
 * event-processor/internal/storage/repository.go). It is never inferred here
 * from device brand, reader hardware, or any other device-level signal —
 * doing so previously caused real entrada/saida/passagem zones to be
 * misclassified as exits based on assumptions about specific hardware quirks
 * (e.g. "ControlID always reports entrada on its exit reader"). Trust the
 * zone-resolved direction literally; when it's genuinely unknown, defer to
 * the scan fallback (which re-verifies against the same zone-resolved data
 * in the backend) instead of guessing.
 *
 * State transitions:
 *   awaiting_entry  ──[authorized entrada]──► inside
 *   inside          ──[authorized saida ]──► exited
 */

// ── Domain types ──────────────────────────────────────────────────────────────

export interface VisitorRecord {
  personId: string;
  roleId: string;
  name: string;
  /** ISO-8601 — when the visitor entered (only set for 'inside' state) */
  entryTime?: string;
  /** ISO-8601 — when the visitor role was created */
  roleCreatedAt: string;
}

export interface AccessEventInput {
  personId?: string | null;
  /** Device-specific employee hash (MD5 of UUID for Hikvision; numeric string for ControlID) */
  employeeNo?: string | null;
  direction?: string | null;
  isAuthorized: boolean;
}

export type VisitorActionType =
  | { kind: 'check_in';  visitor: VisitorRecord; source: string }
  | { kind: 'check_out'; visitor: VisitorRecord; source: string }
  | { kind: 'scan' }
  | { kind: 'none' };

// ── classifyDirection ─────────────────────────────────────────────────────────

/**
 * Given an event direction string, return which state transitions are possible.
 * Pure function.
 */
export function classifyDirection(direction?: string | null): {
  couldBeEntrada: boolean;
  couldBeSaida: boolean;
} {
  const dir = (direction as string | null | undefined)?.toLowerCase().trim();
  // Literal, zone-resolved values only. 'passagem' (bidirectional pass-through zone),
  // missing direction, or anything else all mean "not a zone-confirmed entrada/saida" —
  // never guess which one it "must" secretly be. The backend's own state machine
  // (ProcessVisitorStateTransition) follows the same rule: it never changes
  // visitor_state on anything but a real, zone-resolved entrada/saida.
  return {
    couldBeEntrada: dir === 'entrada',
    couldBeSaida:   dir === 'saida',
  };
}

// ── resolveAccessEventAction ──────────────────────────────────────────────────

/**
 * Determine what action to take for an incoming access event.
 *
 * Zone-only: an authorized 'entrada' event only ever check-ins an awaiting
 * visitor; an authorized 'saida' event only ever check-outs an inside visitor.
 * There is no cross-inference (e.g. treating 'entrada' as a possible exit) —
 * the direction is already the zone's real entry_type, resolved server-side,
 * so it is trusted at face value. Ambiguous/unresolved directions fall through
 * to 'scan', which re-verifies against the same zone-resolved backend data
 * rather than guessing here.
 *
 * Pure function — no side effects.
 */
export function resolveAccessEventAction(
  event: AccessEventInput,
  awaiting: ReadonlyMap<string, VisitorRecord>,
  inside:   ReadonlyMap<string, VisitorRecord>,
  employeeNoToPersonId: ReadonlyMap<string, string>,
): VisitorActionType {
  if (!event.isAuthorized) return { kind: 'none' };

  const { couldBeEntrada, couldBeSaida } = classifyDirection(event.direction);

  // Build ordered list of person IDs to try (direct → hash fallback)
  const candidates: Array<{ personId: string; isHash: boolean }> = [];
  if (event.personId) {
    candidates.push({ personId: event.personId, isHash: false });
  }
  if (event.employeeNo) {
    const mapped = employeeNoToPersonId.get(event.employeeNo);
    if (mapped && mapped !== event.personId) {
      candidates.push({ personId: mapped, isHash: true });
    }
  }

  for (const { personId, isHash } of candidates) {
    const inInside   = inside.get(personId)   ?? null;
    const inAwaiting = awaiting.get(personId) ?? null;

    if (couldBeSaida && inInside) {
      return {
        kind: 'check_out',
        visitor: inInside,
        source: isHash ? 'SSE-hash' : 'SSE',
      };
    }

    if (couldBeEntrada && inAwaiting) {
      return {
        kind: 'check_in',
        visitor: inAwaiting,
        source: isHash ? 'SSE-hash' : 'SSE',
      };
    }
  }

  // No direct zone-confirmed match — schedule a WS scan if there are visitors worth
  // checking. The scan re-verifies against the backend's zone-resolved access log.
  const hasVisitors = awaiting.size > 0 || inside.size > 0;
  return hasVisitors ? { kind: 'scan' } : { kind: 'none' };
}
